import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Layers, Library } from 'lucide-react';
import {
  FormDialog,
  FormSection,
  FormStatusField,
  FormTextField,
} from '@/components/common/FormDialog';
import {
  bookEditionFormSchema,
  bookTypeFormSchema,
  type BookEdition,
  type BookEditionFormValues,
  type BookType,
  type BookTypeFormValues,
} from '../types/product.types';
import { useSaveBookEdition, useSaveBookType } from '../hooks/useCatalogueMutations';

const BOOK_TYPE_DEFAULTS: BookTypeFormValues = {
  name: '',
  description: '',
  groupNo: '',
  isActive: true,
};

interface BookTypeModalProps {
  bookType: BookType | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function BookTypeFormModal({ bookType, open, onOpenChange }: BookTypeModalProps) {
  const form = useForm<BookTypeFormValues>({
    resolver: zodResolver(bookTypeFormSchema),
    defaultValues: BOOK_TYPE_DEFAULTS,
  });

  useEffect(() => {
    form.reset(
      bookType === null
        ? BOOK_TYPE_DEFAULTS
        : {
            name: bookType.name,
            description: bookType.description ?? '',
            groupNo: bookType.groupNo === null ? '' : String(bookType.groupNo),
            isActive: bookType.isActive,
          },
    );
  }, [bookType, open, form]);

  const save = useSaveBookType(() => onOpenChange(false));

  function submit(values: BookTypeFormValues) {
    const groupNo = values.groupNo.trim() === '' ? undefined : Number(values.groupNo);
    save.mutate({
      id: bookType?.id ?? null,
      payload: {
        name: values.name,
        isActive: values.isActive,
        ...(values.description.trim() === '' ? {} : { description: values.description.trim() }),
        ...(groupNo === undefined || Number.isNaN(groupNo) ? {} : { groupNo }),
      },
    });
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      icon={Library}
      title={bookType === null ? 'Add Book Type' : 'Edit Book Type'}
      description="A book type groups the specimens the field carries."
      form={form}
      isPending={save.isPending}
      isEditMode={bookType !== null}
      onSubmit={submit}
    >
      <FormSection>
        <FormTextField
          control={form.control}
          name="name"
          label="Name"
          placeholder="Text Book"
          isFullRow
        />
        <FormTextField
          control={form.control}
          name="description"
          label="Description"
          placeholder="Optional"
          isFullRow
        />
        <FormTextField
          control={form.control}
          name="groupNo"
          label="Group"
          placeholder="Optional"
          inputMode="numeric"
          hasTabularNumbers
        />
      </FormSection>

      <FormStatusField control={form.control} name="isActive" />
    </FormDialog>
  );
}

const BOOK_EDITION_DEFAULTS: BookEditionFormValues = {
  code: '',
  name: '',
  nameLocal: '',
  description: '',
  isActive: true,
};

interface BookEditionModalProps {
  bookEdition: BookEdition | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function BookEditionFormModal({ bookEdition, open, onOpenChange }: BookEditionModalProps) {
  const form = useForm<BookEditionFormValues>({
    resolver: zodResolver(bookEditionFormSchema),
    defaultValues: BOOK_EDITION_DEFAULTS,
  });

  useEffect(() => {
    form.reset(
      bookEdition === null
        ? BOOK_EDITION_DEFAULTS
        : {
            code: bookEdition.code ?? '',
            name: bookEdition.name,
            nameLocal: bookEdition.nameLocal ?? '',
            description: bookEdition.description ?? '',
            isActive: bookEdition.isActive,
          },
    );
  }, [bookEdition, open, form]);

  const save = useSaveBookEdition(() => onOpenChange(false));

  function submit(values: BookEditionFormValues) {
    save.mutate({
      id: bookEdition?.id ?? null,
      payload: {
        name: values.name,
        isActive: values.isActive,
        ...(values.code.trim() === '' ? {} : { code: values.code.trim() }),
        ...(values.nameLocal.trim() === '' ? {} : { nameLocal: values.nameLocal.trim() }),
        ...(values.description.trim() === '' ? {} : { description: values.description.trim() }),
      },
    });
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      icon={Layers}
      title={bookEdition === null ? 'Add Book Edition' : 'Edit Book Edition'}
      description="An edition is the year or revision a specimen belongs to."
      form={form}
      isPending={save.isPending}
      isEditMode={bookEdition !== null}
      onSubmit={submit}
    >
      <FormSection>
        <FormTextField
          control={form.control}
          name="name"
          label="Name"
          placeholder="2026 Edition"
          isFullRow
        />
        <FormTextField
          control={form.control}
          name="code"
          label="Code"
          placeholder="Optional"
          hasTabularNumbers
        />
        <FormTextField
          control={form.control}
          name="nameLocal"
          label="Local Name"
          placeholder="Optional"
        />
      </FormSection>

      <FormStatusField control={form.control} name="isActive" />
    </FormDialog>
  );
}
